import React from "react";
import { useMutation } from "@apollo/client";
import { gql } from "@apollo/client";
import { Form, FormSpy } from "react-final-form";
import isEqual from "lodash.isequal";
import { AddressField } from "@syos-music/form";

import { Collapse, Typography, Switch, Stack } from "@mui/material";
import { MapMarker as AddressIcon } from "mdi-material-ui";

import PrintFab from "components/common/PrintFab";
import SaveFab from "components/common/SaveFab";

import SidebarItem from "./SidebarItem";

const ADDRESS_FIELDS = [
  "firstName",
  "lastName",
  "vatNumber",
  "street",
  "street2",
  "city",
  "zipCode",
  "state",
  "countryId",
  "phone",
];

const ADDRESS_FRAGMENT = gql`
  fragment Orders_OrderSidebarAddress_address on Address {
    firstName
    lastName
    vatNumber
    street
    street2
    city
    zipCode
    state
    country {
      id
      name
    }
    countryId
    phone
  }
`;

const ORDER_FRAGMENT = gql`
  fragment Orders_OrderSidebarAddress_Order on Order {
    id
    isB2B
    paymentDate

    user {
      id
      roles
    }

    shippingAddress {
      ...Orders_OrderSidebarAddress_address
    }

    billingAddress {
      ...Orders_OrderSidebarAddress_address
    }
  }

  ${ADDRESS_FRAGMENT}
`;

const MUTATION = gql`
  mutation (
    $orderId: ID!
    $shippingAddress: AddressInput
    $billingAddress: AddressInput
  ) {
    UpdateOrderAdmin(
      orderId: $orderId
      shippingAddress: $shippingAddress
      billingAddress: $billingAddress
    ) {
      order {
        id

        ...Orders_OrderSidebarAddress_Order
      }
    }
  }

  ${ORDER_FRAGMENT}
`;

export default function OrderSidebarAddress({ order }) {
  const [updateOrderAddress] = useMutation(MUTATION);

  const handleSubmit = React.useCallback(
    async ({ shippingAddress, billingAddress }) => {
      // React final form removes empty values from the form values
      // This function ensures all address fields are present for the mutation,
      // so we can erase values properly
      // @see https://github.com/final-form/final-form/blob/master/docs/faq.md#why-does--final-form-set-my--field-value-to-undefined
      const formatAddress = (address) =>
        Object.fromEntries(
          ADDRESS_FIELDS.map((fieldName) => [
            fieldName,
            address[fieldName] ?? null,
          ])
        );

      await updateOrderAddress({
        variables: {
          orderId: order.id,
          shippingAddress: formatAddress(shippingAddress),
          billingAddress: formatAddress(billingAddress),
        },
      })
        .then(() => {})
        .catch(() => true);
    },
    [updateOrderAddress, order.id]
  );

  const initialValues = React.useMemo(() => {
    const formatAddress = (address) =>
      Object.entries(address ?? {}).reduce((acc, [key, value]) => {
        if (value != null) acc[key] = value;
        return acc;
      }, {});

    return {
      shippingAddress: formatAddress(order.shippingAddress),
      billingAddress: formatAddress(order.billingAddress),
    };
  }, [order.shippingAddress, order.billingAddress]);

  const [useBillingAddress, setUseBillingAddress] = React.useState(() => {
    const { shippingAddress, billingAddress } = order;
    return !ADDRESS_FIELDS.every(
      (fieldName) =>
        shippingAddress?.[fieldName] === billingAddress?.[fieldName]
    );
  });

  return (
    <SidebarItem icon={<AddressIcon />} title="Adresse">
      <Form
        onSubmit={handleSubmit}
        initialValues={initialValues}
        keepDirtyOnReinitialize
      >
        {({ handleSubmit, pristine, submitting, form, values }) => {
          return (
            <form onSubmit={handleSubmit} noValidate autoComplete="off">
              <Stack spacing={1} sx={{ p: 1 }}>
                <Stack spacing={1} direction="row" alignItems="center">
                  <Typography
                    variant="h4"
                    color="primary"
                    noWrap
                    sx={{ flexGrow: 1 }}
                  >
                    Adresse de livraison
                  </Typography>

                  <PrintFab>
                    <div
                      style={{ width: "6in", height: "4in", fontSize: "22pt" }}
                    >
                      <div>
                        {[
                          order.shippingAddress.firstName,
                          order.shippingAddress.lastName,
                        ]
                          .filter(Boolean)
                          .join(" ")}
                      </div>
                      <div>{order.shippingAddress.street ?? ""}</div>
                      <div>{order.shippingAddress.street2 ?? ""}</div>
                      <div>{order.shippingAddress.state ?? ""}</div>
                      <div>
                        {[
                          order.shippingAddress.zipCode,
                          order.shippingAddress.city,
                        ]
                          .filter(Boolean)
                          .join(" ")}
                      </div>
                      <div>{order.shippingAddress.country.name}</div>
                    </div>
                  </PrintFab>

                  <SaveFab
                    onSave={handleSubmit}
                    disabled={pristine}
                    loading={submitting}
                  />
                </Stack>

                <div>
                  <AddressField
                    name="shippingAddress"
                    withPhone
                    withVatNumber={order.isB2B}
                    fieldsProps={{
                      countryId: {
                        required: true,
                        disabled: !!order.paymentDate,
                      },
                    }}
                  />
                </div>

                <Stack spacing={1} direction="row" alignItems="center">
                  <Typography
                    variant="h4"
                    color="primary"
                    noWrap
                    sx={{ flexGrow: 1 }}
                  >
                    Adresse de facturation
                  </Typography>

                  <Switch
                    checked={useBillingAddress}
                    onChange={(evt, checked) => {
                      setUseBillingAddress(checked);

                      if (!checked) {
                        form.change("billingAddress", values.shippingAddress);
                      }
                    }}
                  />
                </Stack>

                <FormSpy
                  subscription={{ values: true }}
                  onChange={({ values: newValues }) => {
                    if (useBillingAddress) return;

                    const hasChanged = !isEqual(
                      values.shippingAddress,
                      newValues.shippingAddress
                    );
                    if (!hasChanged) return;

                    form.change("billingAddress", newValues.shippingAddress);
                  }}
                />

                <Collapse in={useBillingAddress}>
                  <div>
                    <AddressField
                      name="billingAddress"
                      withVatNumber={order.isB2B}
                      fieldsProps={{
                        country: {
                          required: true,
                          disabled: !!order.paymentDate,
                        },
                      }}
                    />
                  </div>
                </Collapse>
              </Stack>
            </form>
          );
        }}
      </Form>
    </SidebarItem>
  );
}

OrderSidebarAddress.fragments = {
  order: ORDER_FRAGMENT,
};
